import { handleEvents } from 'handle-events';
import throttle from '../fp/throttle';
import invoke from '../fp/invoke';
import root from '../root';
import config from './_config';
import onKeydown from './_onKeydown';
import getElement from './_getElement';

/**
 * @private
 * Executes a callback.
 *
 * @param  {Function} callback - function to execute
 * @param  {any} data - the data passed to the callback
 * @return {any}
 */
function exec(callback, data) {
  if (typeof callback === 'function') callback(data);
  return data;
}

/**
 * @private
 * Detects the current position and sets the boundaries.
 * @see https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
 *
 * @param {Object} autocomplete - an instance of `google.maps.places.Autocomplete`
 */
function geolocate(autocomplete) {
  const maps = root.google.maps;
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition((position) => {
    const geolocation = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
    const circle = new maps.Circle({
      radius: position.coords.accuracy,
      center: geolocation,
    });
    autocomplete.setBounds(circle.getBounds());
  });
}

/**
 * @public
 * Callback that initializes the google maps api script.
 */
export default function initMapsApi() {
  const maps = root.google.maps;
  const inputAddress = getElement(config.inputAddress);
  if (!inputAddress) return;
  handleEvents(inputAddress)
    .off('.initMapsApi')
    .on('focus.initMapsApi', invoke(config.addressOnFocus))
    .on('keydown.initMapsApi', throttle(onKeydown, 1000), true);
  const autocomplete = new maps.places.Autocomplete(inputAddress, {
    componentRestrictions: { country: 'us' },
    types: ['address'],
  });
  autocomplete.setFields(['address_component']);
  autocomplete.addListener('place_changed', () => {
    exec(config.onPlaceChanged, autocomplete);
    exec(config.updateForm, autocomplete);
  });

  if (config.geolocate) {
    geolocate(autocomplete);
  }

  if (config.style) {
    inputAddress.style = config.style;
  }
}
